import React, { useEffect, useState } from 'react';
import { Location } from 'history';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { Button } from '@mui/material';
import { RouteLeavingProps } from './types';
import { Dialog } from 'components';
import { useTranslation } from 'react-i18next';
import { useUserLogoutDialogContext } from 'contexts';

const warnUserOnPageRefresh = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  //note: chrome(v51>) removed support for custom messages in onbefoureunload dialogs
  // the browser will show the default message
  e.returnValue = '';
  return '';
};

const RouteLeavingGuard: React.FunctionComponent<RouteLeavingProps> = ({
  when,
  ignoreHash,
  ignoreBasePath,
}: RouteLeavingProps) => {
  const { closeDialog } = useUserLogoutDialogContext();
  const currentLocation = useLocation();
  const [isDialogVisible, setDialogVisible] = useState<boolean>(false);
  const [lastLocation, setLastLocation] = useState<Location | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState<boolean>(false);
  const history = useHistory();

  const { t } = useTranslation(['common']);

  const shouldBlock = () => {
    //if needed this can be extended to block or allow based on location pathname
    if (when) {
      return true;
    }
    return false;
  };

  const checkPathOnly = (nextLocation: Location) => {
    if (nextLocation.hash.includes('#logout')) {
      // sepcial case for logout. comes here when logout is clicked
      return true;
    }
    if (!ignoreHash) {
      return true;
    }
    if (ignoreBasePath && nextLocation.pathname.startsWith(ignoreBasePath)) {
      return false;
    }
    return currentLocation.pathname !== nextLocation.pathname;
  };

  const handleBlockedNavigation = (nextLocation: Location): boolean => {
    if (!confirmedNavigation && shouldBlock() && checkPathOnly(nextLocation)) {
      setDialogVisible(true);
      setLastLocation(nextLocation);
      return false;
    }
    return true;
  };
  const handleConfirmNavigationClick = () => {
    setDialogVisible(false);
    setConfirmedNavigation(true);
  };
  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      // Navigate to the previous blocked location
      const route = `${lastLocation.pathname}${lastLocation.hash}`;
      history.push(route);
    }
  }, [confirmedNavigation, history, lastLocation]);

  useEffect(() => {
    // Detecting browser closing & refreshing
    window.addEventListener('beforeunload', when ? warnUserOnPageRefresh : () => null);
    return () => {
      window.removeEventListener('beforeunload', warnUserOnPageRefresh);
    };
  }, [when]);

  return (
    <>
      <Prompt when={when} message={handleBlockedNavigation} />
      <Dialog
        open={isDialogVisible}
        title={t('quitEditing')}
        buttons={
          <>
            <Button
              onClick={() => {
                setDialogVisible(false);
                closeDialog();
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={() => handleConfirmNavigationClick()}
              variant='contained'
              color='warning'
            >
              {t('confirm')}
            </Button>
          </>
        }
      >
        <p>{t('quitEditingInfo')}</p>
      </Dialog>
    </>
  );
};

export default RouteLeavingGuard;
